import { useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Calendar,
  Check,
  ChevronRight,
  Clock,
  Copy,
  ExternalLink,
  FolderKanban,
  History,
  KeyRound,
  Lock,
  MessageSquare,
  Package,
  Pencil,
  Receipt,
  Send,
  Shield,
  Trash2,
  X,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { CredentialField } from "../components/CredentialField";
import { ExpensesTabContent } from "@/modules/expenses/components/ExpensesTabContent";
import {
  useAddVaultComment,
  useDeleteVaultComment,
  useDeleteVaultEntry,
  useUpdateVaultEntry,
  useVaultCategories,
  useVaultComments,
  useVaultEntry,
  useVaultLogs,
  useVaultSensitivities,
} from "../hooks/useVault";
import type { VaultEntryDetail } from "../types";

const BORDER = "hsl(210,30%,85%)";

const CATEGORY_COLORS: Record<string, { bg: string; fg: string }> = {
  database: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" },
  server_ssh: { bg: "hsl(38,90%,92%)", fg: "hsl(35,90%,32%)" },
  api_token: { bg: "hsl(262,55%,92%)", fg: "hsl(262,83%,38%)" },
  cloud_service: { bg: "hsl(180,55%,92%)", fg: "hsl(180,72%,28%)" },
  domain_dns: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)" },
  email_service: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" },
  payment_gateway: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)" },
  dev_tools: { bg: "hsl(262,55%,92%)", fg: "hsl(262,83%,38%)" },
  social_media: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" },
  external_link: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)" },
  other: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)" },
};

const SENSITIVITY_COLORS: Record<
  string,
  { bg: string; fg: string; label: string }
> = {
  low: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)", label: "Low" },
  medium: { bg: "hsl(38,90%,92%)", fg: "hsl(35,90%,32%)", label: "Medium" },
  high: { bg: "hsl(22,90%,92%)", fg: "hsl(22,90%,38%)", label: "High" },
  critical: { bg: "hsl(0,70%,92%)", fg: "hsl(0,70%,40%)", label: "Critical" },
};

const ACTION_COLORS: Record<string, { bg: string; fg: string }> = {
  VIEWED: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" },
  REVEALED: { bg: "hsl(38,90%,92%)", fg: "hsl(35,90%,32%)" },
  COPIED: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)" },
  UPDATED: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)" },
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function timeAgo(iso: string | null): string {
  if (!iso) return "Never";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

type DetailTab = "history" | "comments" | "expenses";

export default function VaultDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { data: entry, isLoading } = useVaultEntry(id ?? "");
  const updateMutation = useUpdateVaultEntry(id ?? "");
  const deleteMutation = useDeleteVaultEntry();
  const { data: categories = [] } = useVaultCategories();
  const { data: sensitivities = [] } = useVaultSensitivities();

  const [editMode, setEditMode] = useState(false);
  const [tab, setTab] = useState<DetailTab>("history");
  const [logsPage, setLogsPage] = useState(1);
  const [commentText, setCommentText] = useState("");
  const [externalCopied, setExternalCopied] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const logsQuery = useVaultLogs(id ?? "", logsPage, 10);
  const commentsQuery = useVaultComments(id ?? "");
  const addComment = useAddVaultComment(id ?? "");
  const deleteComment = useDeleteVaultComment(id ?? "");

  // Edit-mode fields
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editCategory, setEditCategory] = useState("other");
  const [editSensitivity, setEditSensitivity] = useState("medium");
  const [editVisibility, setEditVisibility] = useState<"team" | "restricted">(
    "team",
  );
  const [editRestrictedToIds, setEditRestrictedToIds] = useState<string[]>([]);
  const [editCompanyId, setEditCompanyId] = useState("");
  const [editProductId, setEditProductId] = useState("");
  const [editProjectId, setEditProjectId] = useState("");
  const [editExpiry, setEditExpiry] = useState("");
  const [editExternalUrl, setEditExternalUrl] = useState("");
  const [editPort, setEditPort] = useState("");
  const [editDb, setEditDb] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editUsername, setEditUsername] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editApiKey, setEditApiKey] = useState("");
  const [editApiSecret, setEditApiSecret] = useState("");
  const [editToken, setEditToken] = useState("");
  const [editHost, setEditHost] = useState("");

  // Picker data for Visibility (restricted users) + linked entity selects.
  // Only fetched when edit mode is active to avoid unnecessary queries on
  // a read-only view of a sensitive page.
  const { data: usersResult } = useUserList(
    editMode ? { page: 1, limit: 200 } : { page: 1, limit: 1 },
  );
  const userOptions = usersResult?.data ?? [];
  const { data: companiesResult } = useCompanyList(
    editMode ? { page: 1, limit: 200 } : { page: 1, limit: 1 },
  );
  const companyOptions = companiesResult?.data ?? [];
  const { data: productsResult } = useProductList({
    page: 1,
    limit: 200,
    companyId: editMode && editCompanyId ? editCompanyId : undefined,
  });
  const productOptions = productsResult?.data ?? [];
  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 200,
    companyId: editMode && editCompanyId ? editCompanyId : undefined,
    productId: editMode && editProductId ? editProductId : undefined,
  });
  const projectOptions = projectsResult?.data ?? [];

  function enterEdit(e: VaultEntryDetail) {
    setEditTitle(e.title);
    setEditDescription(e.description ?? "");
    setEditCategory(e.category);
    setEditSensitivity(e.sensitivity);
    setEditVisibility((e.visibility as "team" | "restricted") ?? "team");
    setEditRestrictedToIds(e.restrictedToIds ?? []);
    setEditCompanyId(e.companyId ?? "");
    setEditProductId(e.productId ?? "");
    setEditProjectId(e.projectId ?? "");
    setEditExpiry(e.expiryDate ? e.expiryDate.slice(0, 10) : "");
    setEditExternalUrl(e.externalUrl ?? "");
    setEditPort(e.port ?? "");
    setEditDb(e.databaseName ?? "");
    setEditNotes(e.notes ?? "");
    setEditUsername("");
    setEditPassword("");
    setEditApiKey("");
    setEditApiSecret("");
    setEditToken("");
    setEditHost("");
    setEditMode(true);
  }

  function cancelEdit() {
    setEditMode(false);
    clearEditParam();
  }

  async function saveEdit() {
    if (!entry) return;
    await updateMutation.mutateAsync({
      title: editTitle.trim(),
      description: editDescription.trim() || null,
      category: editCategory,
      sensitivity: editSensitivity,
      visibility: editVisibility,
      restrictedToIds: editVisibility === "restricted" ? editRestrictedToIds : [],
      companyId: editCompanyId || null,
      productId: editProductId || null,
      projectId: editProjectId || null,
      expiryDate: editExpiry ? new Date(editExpiry).toISOString() : null,
      externalUrl: entry.isExternal
        ? editExternalUrl.trim() || null
        : undefined,
      port: !entry.isExternal ? editPort.trim() || null : undefined,
      databaseName: !entry.isExternal ? editDb.trim() || null : undefined,
      notes: editNotes.trim() || null,
      // Only send credential fields if they were typed (otherwise leave existing)
      ...(editUsername.trim() ? { username: editUsername.trim() } : {}),
      ...(editPassword.trim() ? { password: editPassword.trim() } : {}),
      ...(editApiKey.trim() ? { apiKey: editApiKey.trim() } : {}),
      ...(editApiSecret.trim() ? { apiSecret: editApiSecret.trim() } : {}),
      ...(editToken.trim() ? { token: editToken.trim() } : {}),
      ...(editHost.trim() ? { host: editHost.trim() } : {}),
    });
    setEditMode(false);
    clearEditParam();
  }

  function toggleRestrictedUser(userId: string) {
    setEditRestrictedToIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  }

  function handleDelete() {
    if (!entry) return;
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    if (!entry) return;
    await deleteMutation.mutateAsync(entry.id);
    navigate("/access-vault");
  }

  async function copyExternalUrl() {
    if (!entry?.externalUrl) return;
    try {
      await navigator.clipboard.writeText(entry.externalUrl);
      setExternalCopied(true);
      window.setTimeout(() => setExternalCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  }

  async function submitComment() {
    if (!commentText.trim()) return;
    await addComment.mutateAsync(commentText.trim());
    setCommentText("");
  }

  useEffect(() => {
    setLogsPage(1);
  }, [id]);

  // ?edit=true opens directly into edit mode (matches the User Mgmt
  // pattern). Only applies once per entry load so cancel doesn't snap
  // the user back into edit.
  const editParamAppliedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!entry) return;
    if (searchParams.get("edit") !== "true") return;
    if (editParamAppliedRef.current === entry.id) return;
    editParamAppliedRef.current = entry.id;
    enterEdit(entry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, searchParams]);

  function clearEditParam() {
    if (searchParams.get("edit") === "true") {
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
  }

  const contextChain = useMemo(() => {
    if (!entry) return [] as { label: string; href?: string; icon: typeof Building2; type: string }[];
    const chain: { label: string; href?: string; icon: typeof Building2; type: string }[] = [];
    if (entry.companyId)
      chain.push({
        type: "Company",
        label: entry.companyName ?? entry.companyId,
        href: `/companies/${entry.companyId}`,
        icon: Building2,
      });
    if (entry.productId)
      chain.push({
        type: "Product",
        label: entry.productName ?? entry.productId,
        href: `/products/${entry.productId}`,
        icon: Package,
      });
    if (entry.projectId)
      chain.push({
        type: "Project",
        label: entry.projectName ?? entry.projectId,
        href: `/projects/${entry.projectId}`,
        icon: FolderKanban,
      });
    return chain;
  }, [entry]);

  if (isLoading || !entry) {
    return (
      <AppLayout
        breadcrumb={[
          { label: "PhazeX Hub", href: "/" },
          { label: "Resources" },
          { label: "Access Vault", href: "/access-vault" },
        ]}
      >
        <div className="py-12 text-center text-muted-foreground">
          {isLoading ? "Loading…" : "Vault entry not found"}
        </div>
      </AppLayout>
    );
  }

  const expiryDate = entry.expiryDate ? new Date(entry.expiryDate) : null;
  const expiryDays = expiryDate
    ? Math.floor((expiryDate.getTime() - Date.now()) / (24 * 3600 * 1000))
    : null;
  const expiryExpired = expiryDays !== null && expiryDays < 0;
  const expirySoon = expiryDays !== null && expiryDays >= 0 && expiryDays <= 30;
  const expiryColor = expiryExpired || expirySoon
    ? "hsl(0,70%,40%)"
    : "hsl(142,72%,28%)";

  const catColor = CATEGORY_COLORS[entry.category] ?? CATEGORY_COLORS.other;
  const catLabel =
    categories.find((c) => c.value === entry.category)?.label ?? entry.category;
  const sensColor = SENSITIVITY_COLORS[entry.sensitivity] ?? SENSITIVITY_COLORS.medium;

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Resources" },
        { label: "Access Vault", href: "/access-vault" },
        { label: entry.code },
      ]}
    >
      {/* Page Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/access-vault")}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="Back"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <KeyRound
            className="h-[18px] w-[18px] text-foreground"
            strokeWidth={1.5}
          />
          <h1 className="text-xl font-semibold text-foreground">View Entry</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => (editMode ? cancelEdit() : enterEdit(entry))}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title={editMode ? "Exit edit mode" : "Edit"}
          >
            <Pencil
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(0,70%,96%)]"
            title="Delete"
          >
            <Trash2
              className="h-4 w-4 text-[hsl(0,70%,45%)]"
              strokeWidth={1.5}
            />
          </button>
        </div>
      </div>

      {/* ROW 1 — Summary Header Card */}
      <div
        className="mb-4 rounded-xl border bg-white"
        style={{ borderColor: BORDER, padding: "16px 20px" }}
      >
        <h1
          style={{ fontSize: "20px", fontWeight: 700, margin: 0 }}
          className="leading-tight text-foreground"
        >
          {entry.title}
        </h1>

        <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
          <ReferenceIdBadge id={entry.code} />
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
            style={{ background: catColor.bg, color: catColor.fg }}
          >
            {catLabel}
          </span>
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
            style={{ background: sensColor.bg, color: sensColor.fg }}
          >
            {sensColor.label}
          </span>
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
            style={
              entry.visibility === "restricted"
                ? {
                    background: "hsl(38,90%,92%)",
                    color: "hsl(35,90%,32%)",
                  }
                : {
                    background: "hsl(210,55%,92%)",
                    color: "hsl(210,100%,28%)",
                  }
            }
          >
            {entry.visibility === "restricted" ? "Restricted" : "Team"}
          </span>
          {entry.isExternal && (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium"
              style={{
                background: "hsl(210,16%,93%)",
                color: "hsl(215,16%,40%)",
              }}
            >
              <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
              External Link
            </span>
          )}
        </div>

        {expiryDate && (
          <div
            className="mt-2 inline-flex items-center gap-1.5 text-[12px]"
            style={{ color: expiryColor }}
          >
            <Calendar className="h-3.5 w-3.5" strokeWidth={1.5} />
            <span>Expires {formatDate(entry.expiryDate)}</span>
            {expiryExpired && (
              <span
                className="ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                style={{
                  background: "hsl(0,70%,94%)",
                  color: "hsl(0,70%,40%)",
                }}
              >
                Expired
              </span>
            )}
            {expirySoon && (
              <span
                className="ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                style={{
                  background: "hsl(38,90%,94%)",
                  color: "hsl(35,90%,32%)",
                }}
              >
                Expiring soon
              </span>
            )}
          </div>
        )}

        {contextChain.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
            {contextChain.map((c, i) => {
              const Icon = c.icon;
              return (
                <span key={i} className="inline-flex items-center gap-1.5">
                  {i > 0 && (
                    <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
                  )}
                  <Icon className="h-3 w-3" strokeWidth={1.5} />
                  {c.href ? (
                    <Link to={c.href} className="hover:text-[hsl(210,100%,35%)]">
                      {c.label}
                    </Link>
                  ) : (
                    <span>{c.label}</span>
                  )}
                </span>
              );
            })}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-3 text-[12px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span
              className="flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold"
              style={{
                background: "hsl(210,55%,92%)",
                color: "hsl(210,100%,28%)",
              }}
            >
              {initialsOf(entry.createdByName ?? "?")}
            </span>
            Created by {entry.createdByName ?? "Unknown"}
          </span>
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" strokeWidth={1.5} />
            {formatDate(entry.createdAt)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" strokeWidth={1.5} />
            Last accessed: {timeAgo(entry.lastAccessedAt)}
            {entry.lastAccessedByName && ` by ${entry.lastAccessedByName}`}
          </span>
        </div>
      </div>

      {/* ROW 2 — Credentials + Details */}
      <div
        className="mb-4 grid gap-4"
        style={{ gridTemplateColumns: "60fr 40fr" }}
      >
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER, padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center justify-between">
            <span
              style={{
                fontSize: "13px",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Lock className="h-3.5 w-3.5" strokeWidth={1.5} />
              Credentials
            </span>
            <div style={{ display: "flex", gap: "4px" }}>
              {editMode ? (
                <>
                  <button
                    type="button"
                    onClick={saveEdit}
                    disabled={updateMutation.isPending}
                    style={{
                      width: "28px",
                      height: "28px",
                      border: "1px solid hsl(173,50%,70%)",
                      borderRadius: "4px",
                      background: "hsl(173,50%,94%)",
                      color: "hsl(173,100%,25%)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title="Save changes"
                  >
                    <Check size={14} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    style={{
                      width: "28px",
                      height: "28px",
                      border: "1px solid hsl(0,50%,80%)",
                      borderRadius: "4px",
                      background: "hsl(0,50%,96%)",
                      color: "hsl(0,84%,50%)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title="Cancel"
                  >
                    <X size={14} strokeWidth={2} />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => enterEdit(entry)}
                  style={{
                    width: "28px",
                    height: "28px",
                    border: "1px solid hsl(210,30%,85%)",
                    borderRadius: "4px",
                    background: "transparent",
                    color: "hsl(215,16%,47%)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Edit"
                >
                  <Pencil size={14} strokeWidth={1.5} />
                </button>
              )}
            </div>
          </div>

          <div className="mb-3 text-[11px] italic text-muted-foreground">
            🔒 All values are encrypted. Click reveal to view.
          </div>

          {/* Honeypot pair — absorbs Chrome / 1Password autofill before
              the real credential inputs render below. Critical: without
              these, the password-type inputs in CredentialField pick up
              the user's saved login password the moment edit mode opens.
              See companion attributes inside CredentialField. */}
          {editMode && (
            <>
              <input
                type="text"
                name="username"
                autoComplete="username"
                tabIndex={-1}
                aria-hidden="true"
                style={{
                  position: "absolute",
                  opacity: 0,
                  height: 0,
                  width: 0,
                  pointerEvents: "none",
                }}
              />
              <input
                type="password"
                name="password"
                autoComplete="current-password"
                tabIndex={-1}
                aria-hidden="true"
                style={{
                  position: "absolute",
                  opacity: 0,
                  height: 0,
                  width: 0,
                  pointerEvents: "none",
                }}
              />
            </>
          )}

          {editMode && (
            <div className="mb-3 grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[11px] uppercase text-muted-foreground">
                  Title
                </label>
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] uppercase text-muted-foreground">
                  Category
                </label>
                <Select
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-[11px] uppercase text-muted-foreground">
                  Sensitivity
                </label>
                <Select
                  value={editSensitivity}
                  onChange={(e) => setEditSensitivity(e.target.value)}
                >
                  {sensitivities.map((s) => (
                    <option key={s.id} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-[11px] uppercase text-muted-foreground">
                  Expiry Date
                </label>
                <Input
                  type="date"
                  value={editExpiry}
                  onChange={(e) => setEditExpiry(e.target.value)}
                />
              </div>
              <div className="col-span-2 space-y-1">
                <label className="text-[11px] uppercase text-muted-foreground">
                  Description
                </label>
                <textarea
                  rows={2}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  style={{ borderColor: BORDER }}
                />
              </div>
            </div>
          )}

          {entry.isExternal ? (
            <div className="space-y-3">
              <div>
                <div className="text-[11px] uppercase text-muted-foreground mb-1">
                  External URL
                </div>
                {editMode ? (
                  <Input
                    type="url"
                    value={editExternalUrl}
                    onChange={(e) => setEditExternalUrl(e.target.value)}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <a
                      href={entry.externalUrl ?? "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 break-all text-sm text-primary hover:underline"
                    >
                      {entry.externalUrl}
                    </a>
                    <button
                      type="button"
                      onClick={copyExternalUrl}
                      className="flex h-7 w-7 items-center justify-center rounded border hover:bg-muted"
                      style={{ borderColor: BORDER }}
                      title="Copy URL"
                    >
                      {externalCopied ? (
                        <Check
                          className="h-3.5 w-3.5 text-[hsl(142,72%,28%)]"
                          strokeWidth={1.5}
                        />
                      ) : (
                        <Copy
                          className="h-3.5 w-3.5 text-muted-foreground"
                          strokeWidth={1.5}
                        />
                      )}
                    </button>
                    <a
                      href={entry.externalUrl ?? "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex h-7 items-center gap-1 rounded border px-2 text-xs hover:bg-muted"
                      style={{ borderColor: BORDER }}
                      title="Open Link"
                    >
                      <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
                      Open Link
                    </a>
                  </div>
                )}
              </div>
              <NotesSection
                value={entry.notes}
                editMode={editMode}
                editValue={editNotes}
                onEditChange={setEditNotes}
              />
            </div>
          ) : (
            <div className="space-y-1">
              <CredentialField
                label="Username"
                vaultId={entry.id}
                field="username"
                hasValue={entry.hasUsername}
                isEditMode={editMode}
                editValue={editUsername}
                onEditChange={setEditUsername}
              />
              <CredentialField
                label="Password"
                vaultId={entry.id}
                field="password"
                hasValue={entry.hasPassword}
                isPasswordType
                isEditMode={editMode}
                editValue={editPassword}
                onEditChange={setEditPassword}
              />
              <CredentialField
                label="API Key"
                vaultId={entry.id}
                field="apiKey"
                hasValue={entry.hasApiKey}
                isEditMode={editMode}
                editValue={editApiKey}
                onEditChange={setEditApiKey}
              />
              <CredentialField
                label="API Secret"
                vaultId={entry.id}
                field="apiSecret"
                hasValue={entry.hasApiSecret}
                isPasswordType
                isEditMode={editMode}
                editValue={editApiSecret}
                onEditChange={setEditApiSecret}
              />
              <CredentialField
                label="Token"
                vaultId={entry.id}
                field="token"
                hasValue={entry.hasToken}
                isPasswordType
                isEditMode={editMode}
                editValue={editToken}
                onEditChange={setEditToken}
              />
              <CredentialField
                label="Host"
                vaultId={entry.id}
                field="host"
                hasValue={entry.hasHost}
                isEditMode={editMode}
                editValue={editHost}
                onEditChange={setEditHost}
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "8px 0",
                }}
              >
                <div
                  style={{
                    width: "120px",
                    fontSize: "11px",
                    textTransform: "uppercase",
                    color: "hsl(215,16%,47%)",
                    fontWeight: 500,
                  }}
                >
                  Port
                </div>
                <div style={{ flex: 1 }}>
                  {editMode ? (
                    <Input
                      type="number"
                      value={editPort}
                      onChange={(e) => setEditPort(e.target.value)}
                    />
                  ) : (
                    <span className="font-mono text-sm">
                      {entry.port ?? "—"}
                    </span>
                  )}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "8px 0",
                }}
              >
                <div
                  style={{
                    width: "120px",
                    fontSize: "11px",
                    textTransform: "uppercase",
                    color: "hsl(215,16%,47%)",
                    fontWeight: 500,
                  }}
                >
                  Database
                </div>
                <div style={{ flex: 1 }}>
                  {editMode ? (
                    <Input
                      value={editDb}
                      onChange={(e) => setEditDb(e.target.value)}
                    />
                  ) : (
                    <span className="font-mono text-sm">
                      {entry.databaseName ?? "—"}
                    </span>
                  )}
                </div>
              </div>

              <NotesSection
                value={entry.notes}
                editMode={editMode}
                editValue={editNotes}
                onEditChange={setEditNotes}
              />
            </div>
          )}
        </div>

        {/* RIGHT — Details & Access */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER, padding: "16px 20px" }}
        >
          <h3 className="mb-3 text-[13px] font-semibold text-foreground">
            Entry Details
          </h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <Field label="Category">
              <span className="text-foreground">{catLabel}</span>
            </Field>
            <Field label="Sensitivity">
              <span className="text-foreground">{sensColor.label}</span>
            </Field>
            <Field label="Visibility">
              {editMode ? (
                <Select
                  value={editVisibility}
                  onChange={(e) =>
                    setEditVisibility(
                      e.target.value as "team" | "restricted",
                    )
                  }
                >
                  <option value="team">Team</option>
                  <option value="restricted">Restricted</option>
                </Select>
              ) : (
                <span className="text-foreground capitalize">
                  {entry.visibility}
                </span>
              )}
            </Field>
            <Field label="Expiry Date">
              <span className="text-foreground">
                {formatDate(entry.expiryDate)}
              </span>
            </Field>
            <Field label="Created By">
              <span className="text-foreground">
                {entry.createdByName ?? "—"}
              </span>
            </Field>
            <Field label="Created At">
              <span className="text-foreground">
                {formatDateTime(entry.createdAt)}
              </span>
            </Field>
            <Field label="Last Updated">
              <span className="text-foreground">
                {formatDateTime(entry.updatedAt)}
              </span>
            </Field>
          </div>

          <div
            className="my-4 border-t"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          />

          <h3 className="mb-2 text-[13px] font-semibold text-foreground">
            Access Stats
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Accesses</span>
              <span className="font-medium">{entry.accessCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Last Accessed</span>
              <span>
                {timeAgo(entry.lastAccessedAt)}
                {entry.lastAccessedByName && ` by ${entry.lastAccessedByName}`}
              </span>
            </div>
          </div>

          <div
            className="my-4 border-t"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          />

          <h3 className="mb-2 text-[13px] font-semibold text-foreground">
            Entity Context
          </h3>
          {editMode ? (
            <div className="space-y-2 text-sm">
              <div className="space-y-1">
                <Label htmlFor="vlt-edit-company">Company</Label>
                <Select
                  id="vlt-edit-company"
                  value={editCompanyId}
                  onChange={(e) => {
                    setEditCompanyId(e.target.value);
                    setEditProductId("");
                    setEditProjectId("");
                  }}
                >
                  <option value="">— None —</option>
                  {companyOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="vlt-edit-product">Product</Label>
                <Select
                  id="vlt-edit-product"
                  value={editProductId}
                  onChange={(e) => {
                    setEditProductId(e.target.value);
                    setEditProjectId("");
                  }}
                  disabled={!editCompanyId}
                >
                  <option value="">— None —</option>
                  {productOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="vlt-edit-project">Project</Label>
                <Select
                  id="vlt-edit-project"
                  value={editProjectId}
                  onChange={(e) => setEditProjectId(e.target.value)}
                >
                  <option value="">— None —</option>
                  {projectOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          ) : contextChain.length === 0 ? (
            <div className="text-[12px] italic text-muted-foreground">
              Not linked to any entity
            </div>
          ) : (
            <div className="space-y-1.5 text-sm">
              {contextChain.map((c, i) => {
                const Icon = c.icon;
                return (
                  <div key={i} className="flex items-center gap-2">
                    <Icon
                      className="h-3.5 w-3.5 text-muted-foreground"
                      strokeWidth={1.5}
                    />
                    <span className="text-muted-foreground">{c.type}:</span>
                    {c.href ? (
                      <Link
                        to={c.href}
                        className="text-foreground hover:text-[hsl(210,100%,35%)]"
                      >
                        {c.label}
                      </Link>
                    ) : (
                      <span className="text-foreground">{c.label}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {editMode && editVisibility === "restricted" && (
            <>
              <div
                className="my-4 border-t"
                style={{ borderColor: "hsl(210,30%,90%)" }}
              />
              <h3 className="mb-2 text-[13px] font-semibold text-foreground">
                Accessible by
              </h3>
              <div
                className="max-h-[180px] overflow-y-auto rounded-md border p-2"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              >
                {userOptions.length === 0 ? (
                  <div className="px-2 py-1 text-xs text-muted-foreground">
                    No users available
                  </div>
                ) : (
                  userOptions.map((u) => (
                    <label
                      key={u.id}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted/40"
                    >
                      <input
                        type="checkbox"
                        checked={editRestrictedToIds.includes(u.id)}
                        onChange={() => toggleRestrictedUser(u.id)}
                        className="h-3.5 w-3.5"
                      />
                      <span>{u.name}</span>
                    </label>
                  ))
                )}
              </div>
            </>
          )}

          {!editMode &&
            entry.visibility === "restricted" &&
            entry.restrictedToNames.length > 0 && (
              <>
                <div
                  className="my-4 border-t"
                  style={{ borderColor: "hsl(210,30%,90%)" }}
                />
                <h3 className="mb-2 text-[13px] font-semibold text-foreground">
                  Accessible by
                </h3>
                <div className="space-y-1 text-sm">
                  {entry.restrictedToNames.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center gap-2"
                      style={{
                        background:
                          u.id === user?.id
                            ? "hsl(210,55%,96%)"
                            : "transparent",
                        borderRadius: 4,
                        padding: "2px 6px",
                      }}
                    >
                      <span
                        className="flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold"
                        style={{
                          background: "hsl(210,55%,92%)",
                          color: "hsl(210,100%,28%)",
                        }}
                      >
                        {initialsOf(u.name)}
                      </span>
                      <span className="text-foreground">
                        {u.name}
                        {u.id === user?.id && (
                          <span className="ml-1 text-[10px] text-muted-foreground">
                            (you)
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
        </div>
      </div>

      {/* ROW 3 — Tabs */}
      <div
        className="mb-4 overflow-hidden rounded-xl border bg-white"
        style={{ borderColor: BORDER }}
      >
        <div
          className="flex items-center border-b"
          style={{ borderColor: "hsl(210,30%,90%)" }}
        >
          <TabBtn
            active={tab === "history"}
            onClick={() => setTab("history")}
            label="Access History"
            count={logsQuery.data?.pagination.total ?? 0}
            icon={History}
          />
          <TabBtn
            active={tab === "comments"}
            onClick={() => setTab("comments")}
            label="Comments"
            count={commentsQuery.data?.length ?? 0}
            icon={MessageSquare}
          />
          <TabBtn
            active={tab === "expenses"}
            onClick={() => setTab("expenses")}
            label="Related Expenses"
            count={entry.expensesCount}
            icon={Receipt}
          />
        </div>

        <div className="p-4">
          {tab === "history" && (
            <div>
              {logsQuery.isLoading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Loading…
                </div>
              ) : (logsQuery.data?.data.length ?? 0) === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  No access history yet.
                </div>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead
                    className="border-b text-[11px] font-semibold uppercase text-muted-foreground"
                    style={{ borderColor: "hsl(210,30%,90%)" }}
                  >
                    <tr>
                      <th className="px-2 py-2">Action</th>
                      <th className="px-2 py-2">Field</th>
                      <th className="px-2 py-2">User</th>
                      <th className="px-2 py-2">IP</th>
                      <th className="px-2 py-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsQuery.data?.data.map((log) => {
                      const c = ACTION_COLORS[log.action] ?? ACTION_COLORS.UPDATED;
                      return (
                        <tr
                          key={log.id}
                          className="border-b"
                          style={{ borderColor: "hsl(210,30%,92%)" }}
                        >
                          <td className="px-2 py-2">
                            <span
                              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
                              style={{ background: c.bg, color: c.fg }}
                            >
                              {log.action}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-xs text-muted-foreground">
                            {log.field ?? "—"}
                          </td>
                          <td className="px-2 py-2">
                            <div className="flex items-center gap-1.5">
                              <span
                                className="flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold"
                                style={{
                                  background: "hsl(210,55%,92%)",
                                  color: "hsl(210,100%,28%)",
                                }}
                              >
                                {initialsOf(log.userName ?? "?")}
                              </span>
                              <span>{log.userName ?? "Unknown"}</span>
                            </div>
                          </td>
                          <td className="px-2 py-2 font-mono text-[11px] text-muted-foreground">
                            {log.ipAddress ?? "—"}
                          </td>
                          <td className="px-2 py-2 text-xs text-muted-foreground">
                            {timeAgo(log.createdAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {(logsQuery.data?.pagination.totalPages ?? 1) > 1 && (
                <div className="mt-3 flex items-center justify-end gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => setLogsPage((p) => Math.max(1, p - 1))}
                    disabled={logsPage <= 1}
                    className="h-7 rounded border px-2 disabled:opacity-40"
                    style={{ borderColor: BORDER }}
                  >
                    ‹ Prev
                  </button>
                  <span>
                    Page {logsPage} of{" "}
                    {logsQuery.data?.pagination.totalPages ?? 1}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setLogsPage((p) =>
                        Math.min(
                          logsQuery.data?.pagination.totalPages ?? 1,
                          p + 1,
                        ),
                      )
                    }
                    disabled={
                      logsPage >= (logsQuery.data?.pagination.totalPages ?? 1)
                    }
                    className="h-7 rounded border px-2 disabled:opacity-40"
                    style={{ borderColor: BORDER }}
                  >
                    Next ›
                  </button>
                </div>
              )}
            </div>
          )}

          {tab === "expenses" && entry && (
            <ExpensesTabContent accessVaultId={entry.id} />
          )}

          {tab === "comments" && (
            <div className="flex flex-col">
              <div className="max-h-[300px] overflow-y-auto">
                {(!commentsQuery.data || commentsQuery.data.length === 0) && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No comments yet. Add operational notes about this entry.
                  </div>
                )}
                {[...(commentsQuery.data ?? [])].reverse().map((c) => {
                  const isOwn = c.authorId === user?.id;
                  return (
                    <div
                      key={c.id}
                      className="border-b py-3 last:border-b-0"
                      style={{ borderColor: "hsl(210,30%,93%)" }}
                    >
                      <div className="flex items-start gap-2">
                        <span
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                          style={{
                            background: "hsl(210,55%,92%)",
                            color: "hsl(210,100%,28%)",
                          }}
                        >
                          {initialsOf(c.authorName ?? "??")}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[13px] font-semibold text-foreground">
                              {c.authorName ?? "Unknown"}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {timeAgo(c.createdAt)}
                            </span>
                            {isOwn && (
                              <button
                                type="button"
                                onClick={() => deleteComment.mutate(c.id)}
                                className="ml-auto text-muted-foreground hover:text-[hsl(0,70%,45%)]"
                                title="Delete"
                              >
                                <Trash2
                                  className="h-3 w-3"
                                  strokeWidth={1.5}
                                />
                              </button>
                            )}
                          </div>
                          <div className="mt-1 whitespace-pre-wrap text-[14px] text-foreground">
                            {c.content}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                className="mt-3 flex items-end gap-2 border-t pt-3"
                style={{ borderColor: "hsl(210,30%,90%)" }}
              >
                <textarea
                  rows={2}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.ctrlKey && e.key === "Enter") {
                      e.preventDefault();
                      submitComment();
                    }
                  }}
                  placeholder="Operational notes about this entry… (Ctrl+Enter to submit)"
                  className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                <button
                  type="button"
                  onClick={submitComment}
                  disabled={!commentText.trim() || addComment.isPending}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-white disabled:opacity-50"
                  style={{ background: "hsl(210,100%,45%)" }}
                  title="Send"
                >
                  <Send className="h-3.5 w-3.5" strokeWidth={1.8} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ROW 4 — Security Notice */}
      <div
        className="rounded-xl border"
        style={{
          borderColor: "hsl(38,90%,75%)",
          background: "hsl(38,90%,97%)",
          padding: "14px 18px",
        }}
      >
        <div className="flex items-start gap-3">
          <Shield
            className="h-5 w-5 shrink-0 text-[hsl(35,90%,32%)]"
            strokeWidth={1.5}
          />
          <div>
            <div className="text-[13px] font-semibold text-[hsl(35,90%,32%)]">
              Security Information
            </div>
            <div className="mt-1.5 text-[12px] text-[hsl(35,70%,30%)]">
              All credentials stored in Access Vault are encrypted using
              AES-256-GCM encryption. Reveal and copy actions are logged in the
              access history above.
            </div>
            <div className="mt-1 text-[12px] text-[hsl(35,70%,30%)]">
              Credentials are automatically hidden 30 seconds after being
              revealed.
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete vault entry?"
        description={entry ? `Are you sure you want to delete '${entry.title}'? It will be removed from your Access Vault.` : ""}
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase text-muted-foreground">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  label,
  count,
  icon: Icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  icon: typeof MessageSquare;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
        active
          ? "border-primary text-primary"
          : "border-transparent text-muted-foreground"
      }`}
    >
      <Icon className="h-4 w-4" strokeWidth={1.5} />
      {label}
      <span
        className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium"
        style={{
          background: active ? "hsl(210,55%,92%)" : "hsl(210,16%,93%)",
          color: active ? "hsl(210,100%,28%)" : "hsl(215,16%,40%)",
        }}
      >
        {count}
      </span>
    </button>
  );
}

function NotesSection({
  value,
  editMode,
  editValue,
  onEditChange,
}: {
  value: string | null;
  editMode: boolean;
  editValue: string;
  onEditChange: (v: string) => void;
}) {
  return (
    <div className="mt-3">
      <div className="text-[11px] uppercase text-muted-foreground mb-1">
        Notes
      </div>
      {editMode ? (
        <textarea
          rows={3}
          value={editValue}
          onChange={(e) => onEditChange(e.target.value)}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          style={{ borderColor: BORDER }}
        />
      ) : (
        <div className="whitespace-pre-wrap text-sm text-foreground">
          {value?.trim() ? value : "—"}
        </div>
      )}
    </div>
  );
}
